"use client";

import React from "react";

import Link from "next/link";
import Image from "next/image";

import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { useInView, useReducedMotion } from "framer-motion";
import { stegaClean } from "@sanity/client/stega";

import { resolvePageLink } from "@/sanity/globals/CTA";
import { IFooter } from "@/sanity/lib/definitions";
import { resolveI18nText, NON_DEFAULT_LANGUAGES, DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";
import { PageBastianKellerSchema } from "@/sanity/pages/PageBastianKellerTemplate";

import DecorationLine from "@/components/global/ui/DecorationLine";
import { MotionDiv } from "@/components/global/utils/motionTags";

import wordmark from "@/public/bastian-keller/wordmark-type.svg";
import arrowDownRight from "@/public/bastian-keller/footer-arrow-down-right.svg";
import arrowUp from "@/public/bastian-keller/footer-arrow-up.svg";

/**
 * The light-theme footer for the Bastian Keller page.
 *
 * The company column reuses the nav's section links; the social links, back-to-top
 * and copyright come from the page document's footer fields. The
 * legal links still come from the shared footer settings, so Impressum and
 * Datenschutz stay in step with the rest of the site. Signed with the page's own
 * wordmark in the layered reveal FooterV2 uses.
 */

const LEGAL_LINK =
  "p-[1rem] font-geistmono font-[400] text-[2rem] leading-none text-black uppercase opacity-70 transition-opacity hover:underline hover:opacity-90";

/**
 * One column entry. At rest it is the label at 60%; on hover the label comes up
 * to full, the arrow fades in and the rule draws out underneath — Figma's
 * "Meine Journey" state.
 */
function FooterLink({ label, href }: { label: string; href: string }) {
  const target = stegaClean(href) || "";
  const isAnchor = target.startsWith("#");

  const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isAnchor) return;
    const section = document.querySelector(target);
    if (!section) return;
    event.preventDefault();
    section.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <a
      href={target || "#"}
      onClick={onClick}
      target={isAnchor ? undefined : "_blank"}
      rel={isAnchor ? undefined : "noopener noreferrer"}
      className="group flex w-full max-w-[24rem] flex-col gap-[0.25rem]">
      <span className="flex w-full items-center gap-[1rem]">
        <span className="min-w-0 flex-1 font-geist text-[2.25rem] leading-[1.5] text-black opacity-60 transition-opacity duration-300 group-hover:opacity-100">
          {label}
        </span>
        <Image
          className="h-[3rem] w-[3rem] shrink-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          src={arrowDownRight}
          alt=""
        />
      </span>
      <span className="block h-px w-0 bg-black transition-[width] duration-300 ease-out group-hover:w-full" />
    </a>
  );
}

export default function Footer({
  data,
  page,
  className,
}: {
  data: IFooter;
  page: PageBastianKellerSchema;
  className?: string;
}) {
  const pathname = usePathname();

  const [currentTime, setCurrentTime] = React.useState<Date | null>(null);

  React.useEffect(() => {
    setCurrentTime(new Date());

    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const prefersReduced = useReducedMotion();
  const layeredRef = React.useRef<HTMLDivElement | null>(null);
  // same cast the page's other scroll hooks use: React 19 types a ref as
  // `RefObject<T | null>`, which framer's `RefObject<Element>` will not take
  const inView = useInView(layeredRef as React.RefObject<Element>, {
    amount: 0.3,
  });

  const language =
    NON_DEFAULT_LANGUAGES.find((l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`) ?? DEFAULT_LANGUAGE;
  const locale = language === "en" ? "en-US" : "de-DE";

  if (!data) return null;

  // the copies only separate on desktop; the mobile frame shows the mark alone
  const vBack = {
    collapsed: { y: "0rem" },
    expanded: { y: "-14rem" },
  };
  const vMid = {
    collapsed: { y: "0rem" },
    expanded: { y: "-7rem" },
  };

  const transition = prefersReduced ? { duration: 0 } : { duration: 0.6, ease: "easeOut" };
  const state = inView ? "expanded" : "collapsed";

  // the company column is the nav's own section links, so the two never drift apart
  const columns = [
    {
      title: page?.footerCompanyTitle || "Company",
      links: page?.navItems?.map((item) => ({ label: item?.label, href: item?.anchor })) ?? [],
    },
    {
      title: page?.footerSocialTitle || "Social Media",
      links: page?.footerSocialLinks?.map((link) => ({ label: link?.label, href: link?.url })) ?? [],
    },
  ].filter((column) => column.links.length);

  return (
    <footer className={cn("relative z-10 w-full bg-[#F3F2F1]", className)}>
      {/*
        The two guides run the height of the footer, on the same insets the rest
        of the page uses — 1rem on the mobile frame, 7rem on the desktop one. They
        are the same rule as the hero's, continued rather than reintroduced.
      */}
      <DecorationLine
        className="top-0 left-[1rem] lg:left-[7.125rem]"
        orientation="vertical"
        style="dark"
      />
      <DecorationLine
        className="top-0 right-[1rem] lg:right-[7rem]"
        orientation="vertical"
        style="dark"
      />

      <div className="mx-auto flex w-[min(240rem,100%)] flex-col px-[2.25rem] pt-[3rem] pb-[2rem] lg:px-[9.5rem] lg:pt-[4.625rem] lg:pb-[4rem]">
        <div className="flex w-full items-start justify-center pb-[6rem] lg:justify-between lg:pb-[13rem]">
          {!!columns.length && (
            <div className="flex items-start gap-[8rem]">
              {columns.map((column, index) => (
                <div
                  key={index}
                  className={cn(
                    "flex flex-col gap-[1rem]",
                    index === 0 ? "w-[26.125rem]" : "lg:w-[20.625rem]",
                  )}>
                  <p className="mb-0 font-geistmono text-[1.75rem] leading-none whitespace-nowrap text-black uppercase opacity-[.24]">
                    {column.title}
                  </p>
                  <div className="flex flex-col gap-[1rem]">
                    {column.links.map((link, linkIndex) => (
                      <FooterLink
                        key={linkIndex}
                        label={link?.label}
                        href={link?.href}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* same beveled tile as the nav's menu trigger */}
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: prefersReduced ? "auto" : "smooth" })}
            className="group hidden shrink-0 items-center gap-[1.5rem] lg:flex">
            <span className="font-geistmono text-[1.75rem] leading-none text-black/70 uppercase transition-colors group-hover:text-black">
              {page?.footerBackToTop || "Back to top"}
            </span>
            <span className="block h-[6rem] w-[7.5rem] rounded-[1rem] bg-[#D3D3D2] p-[0.125rem]">
              <span className="block h-full w-full rounded-[0.875rem] bg-white p-[0.25rem]">
                <span className="flex h-full w-full items-center justify-center overflow-hidden rounded-[0.75rem] bg-[#F3F2F1]">
                  <Image
                    className="h-[3rem] w-[3rem] motion-safe:group-hover:animate-arrow-loop-up"
                    src={arrowUp}
                    alt=""
                  />
                </span>
              </span>
            </span>
          </button>
        </div>

        {/* the page's own rule — edge to edge on the mobile frame */}
        <div className="relative -mx-[2.25rem] h-[2px] lg:mx-0">
          <DecorationLine
            className="top-0"
            orientation="horizontal"
            style="dark"
          />
        </div>

        <div className="mb-[3.75rem] flex w-full flex-col items-center gap-[0.75rem] pt-[3.5rem] lg:mb-[16.5rem] lg:h-[4.375rem] lg:flex-row lg:justify-between lg:gap-0 lg:pt-0 lg:mt-[3rem]">
          <p className="order-1 mb-0 flex gap-[1rem] font-geistmono font-[400] text-[2rem] leading-none whitespace-nowrap text-black uppercase opacity-70 lg:order-3 lg:flex-1 lg:basis-0 lg:justify-end">
            {currentTime && (
              <>
                <span>{currentTime.toLocaleDateString(locale, { weekday: "long" })}</span>
                <span>
                  {currentTime.toLocaleTimeString(locale, {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: false,
                    timeZoneName: "short",
                  })}
                </span>
              </>
            )}
          </p>

          <div className="order-2 flex items-center justify-center gap-[1rem] lg:gap-[2rem]">
            {data?.impressumLink && (
              <Link
                className={LEGAL_LINK}
                href={resolvePageLink(data?.impressumLink, language) ?? "/"}
                target={data.impressumLink?.openInNewTab ? "_blank" : "_self"}>
                <span>{resolveI18nText(data?.impressumTitle, data?.impressumTitleI18n, language)}</span>
              </Link>
            )}

            {data?.datenschutzLink && (
              <Link
                className={LEGAL_LINK}
                href={resolvePageLink(data?.datenschutzLink, language) ?? "/"}
                target={data.datenschutzLink?.openInNewTab ? "_blank" : "_self"}>
                <span>{resolveI18nText(data?.datenschutzTitle, data?.datenschutzTitleI18n, language)}</span>
              </Link>
            )}
          </div>

          <p className="order-3 mb-0 text-center font-geistmono font-[400] text-[2rem] leading-none whitespace-nowrap text-black opacity-70 lg:order-1 lg:flex-1 lg:basis-0 lg:text-start">
            ©{new Date().getFullYear()} {page?.footerCopyright || "Bitbull. All rights reserved."}
          </p>
        </div>

        {/*
          Three copies of the wordmark that separate as the block scrolls into
          view — FooterV2's move, kept intact. The two behind carry the footer's
          own ground so each occludes the one under it; at rest they are stacked
          exactly and the mark reads as a single solid piece of type.
        */}
        <div
          ref={layeredRef}
          className="relative">
          <MotionDiv
            className="absolute top-0 left-0 hidden h-full w-full bg-[#F3F2F1] pt-[1.5rem] lg:block"
            variants={vBack}
            initial="collapsed"
            animate={state}
            transition={transition}>
            <Image
              className="!w-full opacity-20"
              src={wordmark}
              alt=""
            />
          </MotionDiv>

          <MotionDiv
            className="absolute top-0 left-0 z-10 hidden h-full w-full bg-[#F3F2F1] pt-[1.5rem] lg:block"
            variants={vMid}
            initial="collapsed"
            animate={state}
            transition={transition}>
            <Image
              className="!w-full opacity-50"
              src={wordmark}
              alt=""
            />
          </MotionDiv>

          <div className="relative z-20 bg-[#F3F2F1] pt-[.5rem] lg:pt-[1.5rem]">
            <Image
              className="!w-full"
              src={wordmark}
              alt="Bastian Keller"
            />
          </div>
        </div>
      </div>
    </footer>
  );
}
